import { Card, Grid } from '@mantine/core'
import { alertLevelOptions } from 'api/alertRules'
import { InputDeviceSelect } from 'components/devices/DeviceSelectInput'
import { InputDatetimePicker } from 'components/form/input/Input_Datetime'
import { alertStatusOptions } from 'data/alertStatus'
import { useDayjs } from 'hooks/useDayjs'
import { useFilterForm } from 'hooks/useFilterForm'
import { Fragment } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BiLoader } from 'react-icons/bi'
import { BsCalendar2Date } from 'react-icons/bs'
import { IoWarningOutline } from 'react-icons/io5'
import { MdRule } from 'react-icons/md'
import { TbSearch } from 'react-icons/tb'
import { capitalizeFirstLetter } from 'shared/utils'
import { z } from 'zod'
import { AutoSubmit, InputSelect, InputText, SelectItemWithIcon } from '..'

const schema = z.object({
  search: z.string().nullish(),
  fromDate: z.string().nullish(),
  toDate: z.string().nullish(),
  status: z.string().nullish(),
  level: z.string().nullish(),
  alertRuleType: z.string().nullish(),
  deviceId: z.string().nullish(),
})

export const AlertsFilters = ({
  noWrapper = false,
  excludeDevice,
}: {
  excludeDevice?: boolean
  noWrapper?: boolean
}) => {
  const { t } = useTranslation()
  const dayjs = useDayjs()
  const { methods, onSubmit } = useFilterForm(schema, {
    status: 'Unresolved',
  })
  const Wrapper = noWrapper ? Fragment : Card
  const alertRuleTypeOptions = [
    { value: 'Standard', label: t('alert.ruleTypeStandard') },
    { value: 'DeviceStatus', label: t('alert.ruleTypeDeviceStatus') },
  ]

  return (
    <FormProvider {...methods}>
      <Wrapper>
        <form onSubmit={onSubmit}>
          <Grid>
            <Grid.Col span={{ base: 12, xs: 4, lg: 2.4 }}>
              <InputText
                name="search"
                icon={TbSearch}
                placeholder={`${t('generic.search')}`}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 4, lg: 2.4 }}>
              <InputSelect
                name="level"
                data={alertLevelOptions(t)}
                placeholder={t('alert.level')!}
                itemComponent={SelectItemWithIcon}
                clearable
                icon={IoWarningOutline}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 4, lg: 2.4 }}>
              <InputSelect
                name="status"
                data={alertStatusOptions(t)}
                placeholder={t('generic.status')!}
                itemComponent={SelectItemWithIcon}
                clearable
                icon={BiLoader}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 4, lg: 2.4 }}>
              <InputSelect
                name="alertRuleType"
                data={alertRuleTypeOptions}
                placeholder={t('alert.ruleType')!}
                clearable
                icon={MdRule}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 4, lg: 2.4 }}>
              <InputDatetimePicker
                placeholder={capitalizeFirstLetter(t('generic.from'))}
                name="fromDate"
                maxDate={dayjs().toDate()}
                clearable
                icon={<BsCalendar2Date />}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 4, lg: 2.4 }}>
              <InputDatetimePicker
                placeholder={capitalizeFirstLetter(t('generic.to'))}
                name="toDate"
                maxDate={dayjs().toDate()}
                icon={<BsCalendar2Date />}
                clearable
              />
            </Grid.Col>
            {excludeDevice ? null : (
              <Grid.Col span={{ base: 12, xs: 4 }}>
                <InputDeviceSelect />
              </Grid.Col>
            )}
          </Grid>
          <AutoSubmit />
        </form>
      </Wrapper>
    </FormProvider>
  )
}
